import { useCallback, useEffect, useState } from 'react';

export type Theme = 'light' | 'dark' | 'system';

const STORAGE_KEY = 'fluent.theme';
const THEMES: readonly Theme[] = ['light', 'dark', 'system'] as const;

function isTheme(value: unknown): value is Theme {
  return typeof value === 'string' && (THEMES as readonly string[]).includes(value);
}

function readStoredTheme(): Theme {
  if (typeof window === 'undefined') return 'system';
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (raw != null && isTheme(raw)) return raw;
  } catch {
    // ignore — private browsing / storage disabled
  }
  return 'system';
}

function applyTheme(theme: Theme) {
  if (typeof document === 'undefined') return;
  const root = document.documentElement;
  if (theme === 'system') {
    root.removeAttribute('data-theme');
  } else {
    root.setAttribute('data-theme', theme);
  }
}

/**
 * Theme state hook. Three modes:
 *   - `"light"` / `"dark"` — explicit, written to localStorage
 *   - `"system"` — honour `prefers-color-scheme` (no attribute on <html>)
 *
 * Writes `[data-theme]` on <html>. Tokens in @fluent/tokens swap automatically.
 * SSR-safe — falls back to "system" when window isn't available.
 */
export function useTheme() {
  const [theme, setThemeState] = useState<Theme>(() => readStoredTheme());

  useEffect(() => {
    applyTheme(theme);
  }, [theme]);

  const setTheme = useCallback((next: Theme) => {
    try {
      if (next === 'system') {
        window.localStorage.removeItem(STORAGE_KEY);
      } else {
        window.localStorage.setItem(STORAGE_KEY, next);
      }
    } catch {
      // ignore
    }
    setThemeState(next);
  }, []);

  const toggle = useCallback(() => {
    setTheme(theme === 'dark' ? 'light' : 'dark');
  }, [theme, setTheme]);

  return { theme, setTheme, toggle } as const;
}

/**
 * Inline <script> string that runs before React mounts, setting the initial
 * `[data-theme]` on <html> from localStorage. Prevents the flash of wrong
 * theme that would otherwise happen between CSS load and React hydration.
 *
 * Usage: inject into index.html's <head> via dangerouslySetInnerHTML or as
 * a string. Self-executing, fails silently.
 */
export const THEME_BOOT_SCRIPT = `(function(){try{var t=localStorage.getItem(${JSON.stringify(
  STORAGE_KEY,
)});if(t==="dark"||t==="light"){document.documentElement.setAttribute("data-theme",t);}}catch(e){}})();`;
